import React, { useMemo } from "react";
import ReactModal from "react-modal";
import { axiosget, axiospost, axiosput } from "../../helpers/httprequest";
import swal from "../../helpers/swal";
import { Form, Formik } from "formik";
import { createOLevelResult } from "../../utils/validations";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FormSkeleton } from "../../components/ui/Skeleton";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import FormSelect from "../../components/ui/FormSelect";
import { FiBook, FiClipboard, FiAward, FiSave, FiX, FiEdit2, FiPlus } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../lib/utils";

// Subject categorization for better organization
const SUBJECT_CATEGORIES = {
  sciences: {
    label: "Sciences",
    subjects: [
      "Biology",
      "Botany",
      "Chemistry",
      "Physics",
      "General Biology",
      "General Science",
      "Additional General Science",
      "Human Biology",
      "Rural Biology",
      "Rural Science",
      "Integrated Science",
      "Health Science",
      "Hygiene",
      "Zoology",
      "Geology",
    ],
  },
  mathematics: {
    label: "Mathematics",
    subjects: [
      "Mathematics",
      "Additional Mathematics",
      "Further Mathematics",
      "Applied Mathematics",
      "Pure and Applied Mathematics",
      "Statistics",
    ],
  },
  languages: {
    label: "Languages",
    subjects: [
      "English Language",
      "Arabic Language",
      "Hausa Language",
      "Igbo",
      "French",
      "Literature in English",
      "Other Nigerian Languages",
    ],
  },
  socialSciences: {
    label: "Social Sciences & Humanities",
    subjects: [
      "Economics",
      "Government",
      "Geography",
      "History",
      "Civic Education",
      "Social Studies",
    ],
  },
  vocational: {
    label: "Vocational & Applied",
    subjects: [
      "Agricultural Science",
      "Animal Husbandry",
      "Commerce",
      "Computer Studies",
      "Data Processing",
      "Financial Accounting",
      "Principles of Accounts",
      "Marketing",
      "Fine and Applied Arts",
      "Music",
      "Teaching Practice",
    ],
  },
  homeEconomics: {
    label: "Home Economics",
    subjects: [
      "Food Economics",
      "Food Management",
      "Food Science",
      "Food and Nutrition",
    ],
  },
  religious: {
    label: "Religious Studies",
    subjects: [
      "CRS/IRS",
      "Islamic History",
      "Qua'anic Studies",
      "Tahfeezul Qur'an",
      "Ulumul Qur'an",
    ],
  },
  general: {
    label: "General Education",
    subjects: ["General Education"],
  },
};

// Grade color coding
const GRADE_CONFIGS = [
  { value: "A1", label: "A1 - Excellent", color: "text-green-700", bgColor: "bg-green-50" },
  { value: "B2", label: "B2 - Very Good", color: "text-blue-700", bgColor: "bg-blue-50" },
  { value: "B3", label: "B3 - Good", color: "text-blue-700", bgColor: "bg-blue-50" },
  { value: "C4", label: "C4 - Credit", color: "text-yellow-700", bgColor: "bg-yellow-50" },
  { value: "C5", label: "C5 - Credit", color: "text-yellow-700", bgColor: "bg-yellow-50" },
  { value: "C6", label: "C6 - Credit", color: "text-yellow-700", bgColor: "bg-yellow-50" },
  { value: "D7", label: "D7 - Pass", color: "text-orange-700", bgColor: "bg-orange-50" },
  { value: "E8", label: "E8 - Pass", color: "text-red-700", bgColor: "bg-red-50" },
  { value: "F9", label: "F9 - Fail", color: "text-red-700", bgColor: "bg-red-50" },
];

const BtnOLevelResult = ({ modules, defaultExamination }) => {
  const [showmodal, setshowmodal] = React.useState(false);
  const queryClient = useQueryClient(); // Access the Query Client
  const { data, isLoading, error } = useQuery({
    queryKey: ["oLevelCompleteResult"], // Same queryKey as Component 1
    queryFn: async () => {
      const req = await axiosget("ms_applicant_sitting");
      return req?.data;
    }, // Function to fetch data if not in cache
  });

  // Transform sitting data into select options
  const examinationOptions = useMemo(() => {
    if (!data || data?.type !== undefined) return [];
    return data.map((r) => ({
      value: r?._id,
      label: `${r?.examinationtype} ${r?.examinationnumber}`,
    }));
  }, [data]);

  // Transform subjects into a flat searchable list (sorted alphabetically within categories)
  const subjectOptions = useMemo(() => {
    const options = [];
    Object.entries(SUBJECT_CATEGORIES).forEach(([key, category]) => {
      // Add all subjects from this category with category prefix for better search
      category.subjects.forEach((subject) => {
        options.push({
          value: subject,
          label: subject,
          category: category.label,
        });
      });
    });
    // Sort alphabetically for easier searching
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  // Transform grades into options with color indicators
  const gradeOptions = useMemo(() => {
    return GRADE_CONFIGS.map((grade) => ({
      value: grade.value,
      label: grade.label,
      ...grade,
    }));
  }, []);

  const _save = async (values, { resetForm, setFieldValue }) => {
    try {
      let req;
      if (modules === undefined) {
        req = await axiospost("ms_applicant_result", values);
      } else {
        req = await axiosput(`ms_applicant_result/${modules?._id}`, values);
      }
      if (req.status !== 200) {
        throw new Error("Sorry, system could not finish processing your request");
      }
      const { data: result } = req;
      if (result && result.type === "success") {
        const { message, type } = result;
        swal(type, message);
        queryClient.invalidateQueries({ queryKey: ["oLevelResult"] });
        queryClient.invalidateQueries({ queryKey: ["oLevelSitting"] });
        queryClient.invalidateQueries({ queryKey: ["dashboardDataset"] });

        // For edit mode, close modal after update
        if (modules) {
          setshowmodal(false);
        } else {
          // For add mode, reset only subject and grade fields, keep examination
          const currentExamination = values.examination;
          resetForm();
          setFieldValue('examination', currentExamination);
        }
      } else {
        throw new Error(result.message);
      }
    } catch ({ message }) {
      swal("warning", message);
    }
  };

  const _show = () => {
    setshowmodal(true);
  };

  return (
    <div>
      <ReactModal
        isOpen={showmodal}
        onRequestClose={() => setshowmodal(false)}
        className="ReactModal"
        overlayClassName="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center px-4 z-50"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="bg-white rounded-xl shadow-2xl w-[95%] max-w-4xl max-h-[95vh] overflow-visible"
          role="document"
        >
          {/* Modern Gradient Header */}
          <div className="relative bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 px-6 py-5 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-lg">
                <FiBook className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h5 className="text-xl font-semibold text-white">
                  {modules ? "Edit O-Level Result" : "Add O-Level Result"}
                </h5>
                <p className="text-primary-100 text-sm mt-0.5">
                  {modules ? "Update examination result details" : "Enter your O-Level examination result"}
                </p>
              </div>
              <button
                onClick={() => setshowmodal(false)}
                className="p-2 rounded-lg hover:bg-white/20 transition-colors"
                aria-label="Close modal"
              >
                <FiX className="h-6 w-6 text-white" />
              </button>
            </div>
          </div>

          {/* Modal Body */}
          <div className="p-6 overflow-visible">
            <AnimatePresence mode="wait">
              {isLoading && (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <FormSkeleton fields={3} />
                </motion.div>
              )}

              {error && (
                <motion.div
                  key="error"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  <Alert variant="danger">
                    <AlertDescription>
                      Local server error: {error?.message || error}
                    </AlertDescription>
                  </Alert>
                </motion.div>
              )}

              {data?.type !== undefined && (
                <motion.div
                  key="api-error"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  <Alert variant="danger">
                    <AlertDescription>{data?.message}</AlertDescription>
                  </Alert>
                </motion.div>
              )}

              {!isLoading && data?.type === undefined && (
                <motion.div
                  key="form"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <Formik
                    initialValues={{
                      examination: modules ? modules?.examination?._id : (defaultExamination || ""),
                      scored: modules ? modules?.scored : "",
                      subject: modules ? modules?.subject : "",
                    }}
                    validationSchema={createOLevelResult}
                    initialTouched={{ zip: true }}
                    onSubmit={_save}
                  >
                    {({ isSubmitting }) => (
                      <Form className="space-y-4">
                        {/* Examination Section */}
                        {!defaultExamination && (
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 text-sm font-medium text-neutral-700 border-b border-neutral-200 pb-2">
                            <FiClipboard className="h-4 w-4 text-primary-600" />
                            <span>Examination Details</span>
                          </div>

                          <FormSelect
                            name="examination"
                            label="Select Examination Sitting"
                            options={examinationOptions}
                            required
                            searchable
                            clearable
                            className="w-full"
                          />
                        </div>
                        )}

                        {/* Subject & Grade Section */}
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 text-sm font-medium text-neutral-700 border-b border-neutral-200 pb-2">
                            <FiAward className="h-4 w-4 text-primary-600" />
                            <span>Subject & Grade Information</span>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormSelect
                              name="subject"
                              label="Select Subject"
                              options={subjectOptions}
                              required
                              searchable
                              clearable
                              helperText="Search or scroll to find your subject"
                            />

                            <FormSelect
                              name="scored"
                              label="Grade Obtained"
                              options={gradeOptions}
                              required
                              searchable={false}
                              clearable
                            />
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 pt-4 border-t border-neutral-200">
                          <button
                            type="button"
                            onClick={() => setshowmodal(false)}
                            className="flex-1 px-4 py-2.5 text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors font-medium flex items-center justify-center gap-2"
                          >
                            <FiX className="h-4 w-4" />
                            Cancel
                          </button>

                          <button
                            disabled={isSubmitting}
                            type="submit"
                            className={cn(
                              "flex-1 px-4 py-2.5 bg-primary-600 text-white rounded-lg font-medium transition-all",
                              "flex items-center justify-center gap-2",
                              "hover:bg-primary-700 active:scale-[0.98]",
                              "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary-600"
                            )}
                          >
                            {isSubmitting ? (
                              <>
                                <motion.div
                                  animate={{ rotate: 360 }}
                                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                  className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full"
                                />
                                <span>Saving...</span>
                              </>
                            ) : (
                              <>
                                <FiSave className="h-4 w-4" />
                                <span>Save Result</span>
                              </>
                            )}
                          </button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </ReactModal>

      {/* Add Button */}
      {!modules && (
        <button
          type="button"
          onClick={_show}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-xl shadow-sm transition-all"
        >
          <FiPlus className="w-4 h-4" />
          Add Result
        </button>
      )}

      {/* Edit button for existing modules */}
      {modules && (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={_show}
          className="h-10 w-10 bg-primary-600 text-white rounded-full shadow-md hover:shadow-lg transition-all flex items-center justify-center"
          aria-label="Edit O-Level result"
        >
          <FiEdit2 className="h-4 w-4" />
        </motion.button>
      )}
    </div>
  );
};

export default BtnOLevelResult;
